import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card, FormInput } from '../components';
import { ContractorPicker } from '../components/ContractorPicker';
import { InvitationLinkSection } from '../components/InvitationLinkSection';
import { adminApi, type AdminCustomerInvitationResult, type ContractorSearchHit } from '../services/api';
import { apiErrorCode } from '../utils/apiError';

const ERROR_KEYS: Record<string, string> = {
  CLIENT_ALREADY_REGISTERED: 'customerSetup.errors.alreadyRegistered',
  CLIENT_ALREADY_LINKED: 'customerSetup.errors.alreadyLinked',
  INVITATION_ALREADY_SENT: 'customerSetup.errors.alreadyInvited',
  EMAIL_ALREADY_EXISTS: 'customerSetup.errors.emailExists',
  CONTRACTOR_NOT_FOUND: 'customerSetup.errors.contractorNotFound'
};

const emptyForm = { name: '', email: '', phone: '' };

/**
 * Create a client = invite the client (S8), on its own page like contractors.
 * The client registers on their own; one contractor is optional. An existing
 * client account is connected to the chosen contractor directly.
 */
export function AdminCustomerCreate() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(emptyForm);
  const [contractor, setContractor] = useState<ContractorSearchHit | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<AdminCustomerInvitationResult | null>(null);
  const change = (patch: Partial<typeof emptyForm>) => setForm(previous => ({ ...previous, ...patch }));

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    if (!form.name.trim() || !form.email.trim() || !event.currentTarget.checkValidity()) {
      setError(t('customerSetup.invalidFields'));
      return;
    }
    setBusy(true);
    try {
      setResult(await adminApi.createCustomerInvitation({
        name: form.name.trim(),
        email: form.email.trim(),
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
        ...(contractor ? { companyId: contractor.id } : {})
      }));
    } catch (failure) {
      const code = apiErrorCode(failure);
      setError(t((code && ERROR_KEYS[code]) || 'customerSetup.createError'));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (!result || busy) return;
    setBusy(true); setError('');
    try {
      setResult({ ...result, invitation: await adminApi.resendCompanyInvitation(result.invitation.id) });
    } catch { setError(t('companySetup.resendError')); }
    finally { setBusy(false); }
  };

  const startOver = () => {
    setForm(emptyForm);
    setContractor(null);
    setResult(null);
    setError('');
  };

  const invitation = result?.invitation;
  return <DashboardLayout>
    <div className="company-setup">
      <Link to="/admin/customers" className="d-inline-flex align-items-center mb-3">{t('customerSetup.back')}</Link>
      <h1 className="h4 mb-2">{t('customerSetup.title')}</h1>
      <p className="text-muted mb-4">{t('customerSetup.description')}</p>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {result && invitation ? <Card>
        {result.outcome === 'linked' ? <>
          <h2 className="h5">{t('customerSetup.linked.title')}</h2>
          <div className={`alert ${invitation.emailStatus === 'sent' ? 'alert-success' : 'alert-warning'}`} role="status" data-testid="customer-setup-result">
            {t('customerSetup.linked.text', { name: invitation.customerName, contractor: invitation.companyName })}{' '}
            {t(invitation.emailStatus === 'sent' ? 'customerSetup.linked.sent' : 'customerSetup.linked.notSent', { email: invitation.contactEmail })}
          </div>
        </> : <>
          <h2 className="h5">{t('customerSetup.invited.title', { name: invitation.customerName })}</h2>
          <div className={`alert ${invitation.emailStatus === 'sent' ? 'alert-success' : 'alert-warning'}`} role="status" data-testid="customer-setup-result">
            {t(invitation.emailStatus === 'sent' ? 'companySetup.sent' : 'customerSetup.invited.notSent', { email: invitation.contactEmail })}
          </div>
          <p>{invitation.companyName
            ? t('customerSetup.invited.withContractor', { contractor: invitation.companyName })
            : t('customerSetup.invited.standalone')}</p>
          <InvitationLinkSection invitation={invitation} personalText={t('customerSetup.inviteLinkPersonal')}
            testId="customer-setup-invitation-link" deliveryTestId="customer-setup-delivery" />
          {invitation.expiresAt && <p>{t('companySetup.expires', { date: new Intl.DateTimeFormat(i18n.language).format(new Date(invitation.expiresAt)) })}</p>}
        </>}
        <div className="d-flex flex-column flex-sm-row flex-wrap gap-2">
          <button className="btn btn-primary" type="button" onClick={startOver}>{t('customerSetup.another')}</button>
          {result.outcome === 'invited' && <button className="btn btn-outline-primary" type="button" disabled={busy} onClick={resend}>{t('companySetup.resend')}</button>}
          <Link className="btn btn-outline-secondary" to="/admin/customer-invitations">{t('adminCustomerInvitations.title')}</Link>
        </div>
      </Card> : <Card>
        <form onSubmit={submit} noValidate>
          <fieldset disabled={busy}>
            <p className="text-muted">{t('customerSetup.requiredHelp')}</p>
            <div className="row">
              <div className="col-12 col-md-6">
                <FormInput name="name" label={t('customerSetup.fields.name')} value={form.name} maxLength={255} required autoComplete="off"
                  onChange={event => change({ name: event.target.value })} />
              </div>
              <div className="col-12 col-md-6">
                <FormInput name="email" type="email" label={t('customerSetup.fields.email')} value={form.email} maxLength={320} required autoComplete="off"
                  onChange={event => change({ email: event.target.value })} />
              </div>
              <div className="col-12 col-md-6">
                <FormInput name="phone" type="tel" label={t('customerSetup.fields.phone')} value={form.phone} maxLength={40} autoComplete="off"
                  onChange={event => change({ phone: event.target.value })} />
              </div>
            </div>
            <ContractorPicker value={contractor} onChange={setContractor} disabled={busy} />
            <p className="small text-muted mt-2 mb-0">{t(contractor ? 'customerSetup.contractor.connected' : 'customerSetup.contractor.optional')}</p>
          </fieldset>
          <div className="d-flex flex-column flex-sm-row gap-2 mt-4">
            <button type="submit" className="btn btn-primary" disabled={busy}>{t(busy ? 'customerSetup.saving' : 'customerSetup.submit')}</button>
            <Link className="btn btn-outline-secondary" to="/admin/customers">{t('common.cancel')}</Link>
          </div>
        </form>
      </Card>}
    </div>
  </DashboardLayout>;
}
